import React, { lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import theme from '~/theme';

const Icons = {
    'Arrow': lazy(() => import('~/components/icons/Arrow')),
    'ThinArrow': lazy(() => import('~/components/icons/ThinArrow')),
    'CheckMark': lazy(() => import('~/components/icons/CheckMark')),
    'Phone': lazy(() => import('~/components/icons/Phone')),
    'PhoneCall': lazy(() => import('~/components/icons/PhoneCall')),
    'HamburgerMenu': lazy(() => import('~/components/icons/HamburgerMenu')),
    'QuoteMarks': lazy(() => import('~/components/icons/QuoteMarks')),
    'ChevronDown': lazy(() => import('~/components/icons/ChevronDown')),
    'ChevronUp': lazy(() => import('~/components/icons/ChevronUp')),
    'Close': lazy(() => import('~/components/icons/Close')),
    'Plus': lazy(() => import('~/components/icons/Plus')),
    'Location': lazy(() => import('~/components/icons/Location')),
    'Illustration/InBay': lazy(() =>
        import('~/components/icons/illustration/InBay')
    ),
    'Illustration/Tunnel': lazy(() =>
        import('~/components/icons/illustration/Tunnel')
    ),
    'Illustration/SelfService': lazy(() =>
        import('~/components/icons/illustration/SelfService')
    ),
    'Illustration/Chemicals': lazy(() =>
        import('~/components/icons/illustration/Chemicals')
    ),
    'Illustration/SitePlan': lazy(() =>
        import('~/components/icons/illustration/SitePlan')
    ),
    'Illustration/BusinessPlan': lazy(() =>
        import('~/components/icons/illustration/BusinessPlan')
    ),
    'Illustration/EquipmentPlan': lazy(() =>
        import('~/components/icons/illustration/EquipmentPlan')
    ),
    'Illustration/Prepare': lazy(() =>
        import('~/components/icons/illustration/Prepare')
    ),
    'Illustration/Construct': lazy(() =>
        import('~/components/icons/illustration/Construct')
    ),
    'Illustration/Design': lazy(() =>
        import('~/components/icons/illustration/Design')
    ),
    'Illustration/Tools': lazy(() =>
        import('~/components/icons/illustration/Tools')
    ),
    'Illustration/Microphone': lazy(() =>
        import('~/components/icons/illustration/Microphone')
    ),
    'Illustration/Location': lazy(() =>
        import('~/components/icons/illustration/Location')
    ),
    'Illustration/Phone': lazy(() =>
        import('~/components/icons/illustration/Phone')
    ),
    'Illustration/Podcast': lazy(() =>
        import('~/components/icons/illustration/Podcast')
    ),
    'Social/Email': lazy(() => import('~/components/icons/social/Email')),
    'Social/Facebook': lazy(() => import('~/components/icons/social/Facebook')),
    'Social/LinkedIn': lazy(() => import('~/components/icons/social/LinkedIn')),
    'Social/Reddit': lazy(() => import('~/components/icons/social/Reddit')),
    'Social/X': lazy(() => import('~/components/icons/social/X')),
    'Social/Instagram': lazy(() =>
        import('~/components/icons/social/Instagram')
    )
};

const Wrapper = styled.i`
    display: inline-block;
    width: ${({ iconSizeMobile, autoWidth }) =>
        autoWidth ? 'auto' : `${iconSizeMobile}px`};
    height: ${({ iconSizeMobile }) => `${iconSizeMobile}px`};

    @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
        width: ${({ iconSizeTablet, autoWidth }) =>
            autoWidth ? 'auto' : `${iconSizeTablet}px`};
        height: ${({ iconSizeTablet }) => `${iconSizeTablet}px`};
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
        width: ${({ iconSize, autoWidth }) =>
            autoWidth ? 'auto' : `${iconSize}px`};
        height: ${({ iconSize }) => `${iconSize}px`};
    }

    svg {
        vertical-align: top;
        height: 100%;
        width: 100%;

        * {
            color: ${({ iconColor }) => iconColor};
        }
    }
`;

const Loader = styled.span`
    display: block;
    font-size: ${({ size }) => `${size}px`};
    height: ${({ size }) => `${size}px`};
    width: ${({ size }) => `${size}px`};
`;

const Icon = ({
    icon,
    size = 18,
    autoWidth = false,
    sizeTablet,
    sizeMobile,
    color = theme.palette.darkBlue,
    className
}) => {
    const IconPath = Icons[icon];

    return (
        <Wrapper
            aria-hidden="true"
            iconSize={size}
            iconSizeTablet={sizeTablet ?? size}
            iconSizeMobile={sizeMobile ?? sizeTablet ?? size}
            iconColor={color}
            {...{ className, autoWidth }}
        >
            <Suspense fallback={<Loader />}>
                <IconPath />
            </Suspense>
        </Wrapper>
    );
};

Icon.propTypes = {
    icon: PropTypes.string.isRequired,
    size: PropTypes.number,
    autoWidth: PropTypes.bool,
    sizeTablet: PropTypes.number,
    sizeMobile: PropTypes.number,
    color: PropTypes.string,
    className: PropTypes.string
};

export default Icon;
